/**
 * Onboarding Context
 *
 * Provides state management for the onboarding flow.
 * Tracks progress, completion status, and user preferences.
 *
 * @module client/src/components/Onboarding/OnboardingContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  target: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
  order: number;
  optional?: boolean;
}

export interface OnboardingState {
  isActive: boolean;
  currentStepIndex: number;
  completedSteps: string[];
  skippedSteps: string[];
  hasCompletedOnboarding: boolean;
}

export interface OnboardingContextType {
  state: OnboardingState;
  steps: OnboardingStep[];
  currentStep: OnboardingStep | null;
  progress: number;
  startOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipStep: () => void;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  goToStep: (stepId: string) => void;
}

const STORAGE_KEY = 'life-alert-onboarding';

const defaultSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Life Alert AI',
    description:
      'Your intelligent health companion. Let us show you around and help you get started.',
    target: '[data-onboarding="welcome"]',
    placement: 'bottom',
    order: 0,
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description:
      'This is your main dashboard where you can see all your health information at a glance.',
    target: '[data-onboarding="dashboard"]',
    placement: 'bottom',
    order: 1,
  },
  {
    id: 'medications',
    title: 'Medication Tracking',
    description:
      'View and manage your medications. AI will remind you when it\'s time to take them.',
    target: '[data-onboarding="medications"]',
    placement: 'right',
    order: 2,
  },
  {
    id: 'adherence',
    title: 'Adherence Statistics',
    description:
      'Track how well you\'re following your medication schedule with detailed analytics.',
    target: '[data-onboarding="adherence"]',
    placement: 'left',
    order: 3,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description:
      'Stay informed with alerts about medications, health updates, and caregiver messages.',
    target: '[data-onboarding="notifications"]',
    placement: 'bottom',
    order: 4,
  },
  {
    id: 'emergency',
    title: 'Emergency Contacts',
    description:
      'Add emergency contacts who will be notified if AI detects any concerns during calls.',
    target: '[data-onboarding="emergency"]',
    placement: 'left',
    order: 5,
  },
  {
    id: 'profile',
    title: 'Your Profile',
    description:
      'Update your profile information and preferences to personalize your experience.',
    target: '[data-onboarding="profile"]',
    placement: 'bottom',
    order: 6,
    optional: true,
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description:
      'You\'re ready to use Life Alert AI. You can always access this tour again from the help menu.',
    target: '[data-onboarding="complete"]',
    placement: 'bottom',
    order: 7,
  },
];

const defaultState: OnboardingState = {
  isActive: false,
  currentStepIndex: 0,
  completedSteps: [],
  skippedSteps: [],
  hasCompletedOnboarding: false,
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('[Onboarding] Failed to load saved state');
    }
    return defaultState;
  });

  const [steps] = useState<OnboardingStep[]>(defaultSteps);

  // Persist state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('[Onboarding] Failed to save state');
    }
  }, [state]);

  const currentStep =
    state.isActive && steps[state.currentStepIndex]
      ? steps[state.currentStepIndex]
      : null;

  const progress =
    steps.length > 0
      ? Math.round(
          ((state.completedSteps.length + state.skippedSteps.length) /
            steps.length) *
            100
        )
      : 0;

  const startOnboarding = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: true,
      currentStepIndex: 0,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => {
      const currentStep = steps[prev.currentStepIndex];
      const newCompletedSteps = currentStep
        ? [...prev.completedSteps, currentStep.id]
        : prev.completedSteps;

      const nextIndex = prev.currentStepIndex + 1;

      if (nextIndex >= steps.length) {
        return {
          ...prev,
          completedSteps: newCompletedSteps,
          isActive: false,
          hasCompletedOnboarding: true,
        };
      }

      return {
        ...prev,
        completedSteps: newCompletedSteps,
        currentStepIndex: nextIndex,
      };
    });
  }, [steps]);

  const previousStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.max(0, prev.currentStepIndex - 1),
    }));
  }, []);

  const skipStep = useCallback(() => {
    setState((prev) => {
      const currentStep = steps[prev.currentStepIndex];
      const newSkippedSteps = currentStep
        ? [...prev.skippedSteps, currentStep.id]
        : prev.skippedSteps;

      const nextIndex = prev.currentStepIndex + 1;

      if (nextIndex >= steps.length) {
        return {
          ...prev,
          skippedSteps: newSkippedSteps,
          isActive: false,
          hasCompletedOnboarding: true,
        };
      }

      return {
        ...prev,
        skippedSteps: newSkippedSteps,
        currentStepIndex: nextIndex,
      };
    });
  }, [steps]);

  const skipOnboarding = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: false,
      hasCompletedOnboarding: true,
    }));
  }, []);

  const completeOnboarding = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: false,
      hasCompletedOnboarding: true,
      completedSteps: steps.map((s) => s.id),
    }));
  }, [steps]);

  const resetOnboarding = useCallback(() => {
    setState(defaultState);
  }, []);

  const goToStep = useCallback(
    (stepId: string) => {
      const stepIndex = steps.findIndex((s) => s.id === stepId);
      if (stepIndex !== -1) {
        setState((prev) => ({
          ...prev,
          isActive: true,
          currentStepIndex: stepIndex,
        }));
      }
    },
    [steps]
  );

  return (
    <OnboardingContext.Provider
      value={{
        state,
        steps,
        currentStep,
        progress,
        startOnboarding,
        nextStep,
        previousStep,
        skipStep,
        skipOnboarding,
        completeOnboarding,
        resetOnboarding,
        goToStep,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

export default OnboardingContext;
